import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { localeFor } from "../../../utils/region";
import {
  accent, navy, textPrimary, textMuted, textSecondary, font, border, surface, surfaceRaised,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

const START_HOUR = 0;
const END_HOUR = 24; // full day — scrolled to business hours by default
const DEFAULT_SCROLL_HOUR = 7;
const DAY_MS = 24 * 60 * 60 * 1000;
const PX_PER_HOUR = 60;
const PX_PER_MIN = PX_PER_HOUR / 60;
const SNAP_MIN = 15;
const GRID_START_MIN = START_HOUR * 60;
const GRID_END_MIN = END_HOUR * 60;
const GRID_HEIGHT = (GRID_END_MIN - GRID_START_MIN) * PX_PER_MIN;
const DAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/**
 * Nine to five, weekdays. Lives here rather than in a page because it is the
 * fallback THIS GRID applies when a profile has no working hours yet — an
 * instructor with none would otherwise see every hour of every day shaded
 * "outside hours" and conclude the calendar was broken.
 */
export const DEFAULT_WORKING_HOURS = {
  sunday: { enabled: false, start: "09:00", end: "17:00" },
  monday: { enabled: true, start: "09:00", end: "17:00" },
  tuesday: { enabled: true, start: "09:00", end: "17:00" },
  wednesday: { enabled: true, start: "09:00", end: "17:00" },
  thursday: { enabled: true, start: "09:00", end: "17:00" },
  friday: { enabled: true, start: "09:00", end: "17:00" },
  saturday: { enabled: false, start: "09:00", end: "17:00" },
};

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Built from LOCAL date parts, never toISOString().
//
// toISOString() converts to UTC. Every column in this grid is a LOCAL midnight,
// so anywhere east of UTC that serialises to the previous day: the column for
// 30 Aug keys as "2026-08-29", while a slot at 14:00 on 30 Aug keys as
// "2026-08-30" — the key of the 31 Aug column. The result is that a block you
// drag out appears on the day AFTER the one you selected, and existing slots
// render a column to the right. West of UTC it happens to work, which is why
// it survived: the seeded instructor is in America/Denver.
const dayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const sameDate = (a, b) => dayKey(a) === dayKey(b);
const snap = (min) => Math.round(min / SNAP_MIN) * SNAP_MIN;
const clampMin = (min, lo, hi) => Math.min(Math.max(min, lo), hi);
const minutesNow = () => {
  const n = new Date();
  return n.getHours() * 60 + n.getMinutes();
};
const formatMin = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const period = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};
const timeToMin = (str) => {
  const [h, m] = String(str || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

// A dragged range is handed to SessionComposer, which is where what it
// becomes is decided — a class, a 1:1 offer, a reservation, or closed time.
//
// This used to be a "Drag to:" brush you armed BEFORE dragging, defaulting to
// "Close this time". The calendar's resting state was therefore a tool whose
// only job is to destroy availability, and the mode lived in a chip at the top
// of the screen, far from the pointer. Nothing is armed now, and nothing is
// created until the composer is submitted.

const STATUS_STYLE = {
  booked: { color: "#2563eb", label: "Booked" },
  confirmed: { color: "#2563eb", label: "Booked" },
  pending: { color: navy, label: "Pending" },
};

// A fixed, deterministic palette for real lessons (booked/pending) so the
// same subject always reads as the same color across the whole calendar.
const SUBJECT_PALETTE = [
  "#2563eb", "#16a34a", "#d97706", "#9333ea", "#0891b2",
  "#dc2626", "#65a30d", "#c026d3", "#0d9488", "#ea580c",
];
const subjectColor = (subject) => {
  if (!subject) return null;
  let hash = 0;
  for (let i = 0; i < subject.length; i++) hash = (hash * 31 + subject.charCodeAt(i)) >>> 0;
  return SUBJECT_PALETTE[hash % SUBJECT_PALETTE.length];
};

const colorFor = (slot) => {
  if (STATUS_STYLE[slot.status]) return subjectColor(slot.subject) || STATUS_STYLE[slot.status].color;
  if (slot.visibility === "reserved") return "#d97706";
  return textMuted; // blocked
};

const labelFor = (slot) => {
  if (STATUS_STYLE[slot.status]) return slot.subject || STATUS_STYLE[slot.status].label;
  if (slot.visibility === "reserved") return "Reserved";
  return "Closed";
};

/**
 * WHAT A LESSON LOOKS LIKE, AS OPPOSED TO A GAP IN THE DIARY.
 *
 * `slots` are exceptions carved out of working hours — closed time, reserved
 * time. `events` are things with a person attached, and they arrive from
 * /tutors/bookings and /school/group-sessions, which carry the student's name,
 * the subject, the lesson number and whether the debrief is still owed. The
 * availability record for a booked lesson carries none of that, so the two are
 * drawn from different sources on purpose and the caller filters `booking_id`
 * slots out rather than drawing each lesson twice.
 *
 * TWO OF THESE FIVE ARE STATES, NOT KINDS, and are encoded structurally:
 * `proposed` is not a type of lesson, it is a lesson that is not real yet, so
 * it is an outline; `owed` is a lesson that happened and is unfinished, so it
 * is hatched. Both share a hue with something else on purpose — red already
 * means "you", and a request and an unwritten debrief are both waiting on
 * this instructor. Spending a sixth hue on them would say they are unrelated.
 */
export const EVENT_KINDS = {
  requested: { color: accent, label: "Needs your answer" },
  confirmed: { color: "#2563eb", label: "Booked" },
  class: { color: "#9333ea", label: "Group class" },
  proposed: { color: textSecondary, label: "Offered", dashed: true },
  owed: { color: accent, label: "Debrief owed", hatched: true },
  done: { color: textMuted, label: "Taught" },
};

/**
 * Side-by-side placement for events that overlap.
 *
 * Lanes are counted per CLUSTER of mutually-overlapping events, not per day.
 * Per-day is the usual shortcut and it shows: one double-booked morning would
 * squeeze every event in that day — including an untroubled evening lesson six
 * hours later — into a half-width column.
 */
export function packEvents(dayEvents) {
  const sorted = [...dayEvents].sort((a, b) => a.start - b.start || b.end - a.end);
  const placed = [];
  let cluster = [];
  let clusterEnd = null;

  const flush = () => {
    if (!cluster.length) return;
    const laneEnds = [];
    const laneOf = new Map();
    cluster.forEach((e) => {
      let i = laneEnds.findIndex((end) => end <= e.start.getTime());
      if (i === -1) { laneEnds.push(0); i = laneEnds.length - 1; }
      laneEnds[i] = e.end.getTime();
      laneOf.set(e, i);
    });
    cluster.forEach((e) => placed.push({ e, lane: laneOf.get(e), lanes: laneEnds.length }));
    cluster = [];
    clusterEnd = null;
  };

  sorted.forEach((e) => {
    if (clusterEnd !== null && e.start.getTime() >= clusterEnd) flush();
    cluster.push(e);
    clusterEnd = Math.max(clusterEnd ?? 0, e.end.getTime());
  });
  flush();
  return placed;
}

/**
 * A real drag-to-select week calendar. Instructors are bookable by default
 * within `workingHours` — this grid is only for carving exceptions out of
 * that default: press-drag (or a plain click for a quick 60-minute block)
 * to close a time off, or reserve it for one named student. Time outside
 * working hours renders shaded and unavailable.
 */
export default function WeekAvailabilityCalendar({ schoolRegion = "UK", slots, events = [], workingHours, closed, onCompose, composing = false, onDeleteSlot, onPickEvent, jumpTo, gridMaxHeight = 560 }) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [drag, setDrag] = useState(null); // { dayKey, startMin, endMin }
  // The range the composer is currently open on, kept only so it stays drawn
  // on the grid behind the dialog — you should be able to see what you are
  // filling a form in about. { day, start, end }
  const [pending, setPending] = useState(null);
  const columnRefs = useRef({});
  const scrollRef = useRef(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * DAY_MS)), [weekStart]);
  const weekEnd = new Date(weekStart.getTime() + 7 * DAY_MS);
  const hourMarks = useMemo(() => Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i), []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = (DEFAULT_SCROLL_HOUR - START_HOUR) * PX_PER_HOUR;
  }, []);

  // Clicking a day in the month view drops back into the week grid AT THAT
  // DATE. The old month view claimed to do this in a comment and only ever
  // switched the tab, so picking the 20th landed you back on this week — the
  // scan and the edit were two screens that did not talk to each other.
  useEffect(() => {
    if (jumpTo) setWeekStart(startOfWeek(jumpTo));
  }, [jumpTo]);

  // The composer closing — saved or cancelled — is what retires the selection.
  useEffect(() => {
    if (!composing) setPending(null);
  }, [composing]);

  const activeSlots = useMemo(() => slots.filter((s) => s.status !== "cancelled"), [slots]);
  const slotsByDay = useMemo(() => {
    const map = {};
    for (const s of activeSlots) {
      const key = dayKey(new Date(s.start_time));
      (map[key] = map[key] || []).push(s);
    }
    return map;
  }, [activeSlots]);

  // Keyed with the same LOCAL dayKey as the slots, never toISOString — see the
  // note on dayKey above; a UTC key puts an evening lesson in the next column.
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      const key = dayKey(e.start);
      (map[key] = map[key] || []).push(e);
    }
    return map;
  }, [events]);

  const workingWindowFor = (day) => {
    const config = workingHours?.[DAY_KEYS[day.getDay()]];
    if (!config?.enabled) return null;
    return { start: timeToMin(config.start), end: timeToMin(config.end) };
  };

  const today = new Date();
  const nowMin = minutesNow();

  const minutesFromDate = (date) => date.getHours() * 60 + date.getMinutes();

  const beginDrag = (day, e) => {
    if (closed) return;
    if (e.button !== undefined && e.button !== 0) return;
    const el = columnRefs.current[dayKey(day)];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let startMin = snap(GRID_START_MIN + (e.clientY - rect.top) / PX_PER_MIN);
    startMin = clampMin(startMin, GRID_START_MIN, GRID_END_MIN);
    if (sameDate(day, today) && startMin < nowMin) return;
    setDrag({ dayKey: dayKey(day), startMin, endMin: startMin });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const updateDrag = (e) => {
    if (!drag) return;
    const el = columnRefs.current[drag.dayKey];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let curMin = snap(GRID_START_MIN + (e.clientY - rect.top) / PX_PER_MIN);
    curMin = clampMin(curMin, GRID_START_MIN, GRID_END_MIN);
    setDrag((d) => (d ? { ...d, endMin: curMin } : d));
  };

  const finishDrag = () => {
    if (!drag) return;
    const day = days.find((d) => dayKey(d) === drag.dayKey);
    let startMin = Math.min(drag.startMin, drag.endMin);
    let endMin = Math.max(drag.startMin, drag.endMin);
    if (endMin - startMin < SNAP_MIN) endMin = Math.min(startMin + 60, GRID_END_MIN);
    if (sameDate(day, today)) startMin = Math.max(startMin, snap(nowMin));
    setDrag(null);
    if (endMin - startMin < SNAP_MIN || !day) return;

    const start = new Date(day);
    start.setHours(0, startMin, 0, 0);
    const end = new Date(day);
    end.setHours(0, endMin, 0, 0);

    // Events are checked as well as slots. Checking only slots let an
    // instructor drag "closed" straight across a confirmed lesson, which the
    // server then refuses — a silent no-op that reads as a broken calendar.
    const overlaps = (slotsByDay[drag.dayKey] || []).some(
      (s) => start < new Date(s.end_time) && end > new Date(s.start_time)
    ) || (eventsByDay[drag.dayKey] || []).some((e) => start < e.end && end > e.start);
    if (overlaps) return;

    // Nothing is created here. The range is handed to the composer — a drag
    // that acts on its own is a drag that acts wrongly the first time somebody
    // scrolls with the button down.
    setPending({ day, start, end });
    onCompose?.({ start, end });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton size="small" onClick={() => setWeekStart((w) => new Date(w.getTime() - 7 * DAY_MS))}>
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary, minWidth: 150, textAlign: "center" }}>
            {weekStart.toLocaleDateString(localeFor(schoolRegion), { day: "numeric", month: "short" })} – {new Date(weekEnd.getTime() - DAY_MS).toLocaleDateString(localeFor(schoolRegion), { day: "numeric", month: "short" })}
          </Typography>
          <IconButton size="small" onClick={() => setWeekStart((w) => new Date(w.getTime() + 7 * DAY_MS))}>
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Chip
            label="Today"
            size="small"
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            sx={{ ml: 0.5, cursor: "pointer", fontFamily: font, fontSize: "11px" }}
          />
        </Box>
      </Box>

      <Box sx={{
        border: `1px solid ${border}`, borderRadius: "14px", overflow: "hidden",
        backgroundColor: surface,
        // Two shadows: a tight one to sit the card on the page, and a wide soft
        // one so it lifts off the cream rather than being pasted onto it.
        boxShadow: "0 1px 2px rgba(26,26,26,0.05), 0 10px 28px rgba(26,26,26,0.045)",
        opacity: closed ? 0.5 : 1, filter: closed ? "grayscale(0.6)" : "none", transition: "opacity 0.15s",
        pointerEvents: closed ? "none" : "auto",
      }}>
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: 700 }}>
            <Box sx={{
              display: "grid", gridTemplateColumns: "52px repeat(7, minmax(90px, 1fr))",
              borderBottom: `1px solid ${border}`, backgroundColor: surfaceRaised,
            }}>
              <Box />
              {days.map((day) => {
                const isToday = sameDate(day, today);
                return (
                  <Box key={dayKey(day)} sx={{ textAlign: "center", py: 1, borderLeft: `1px solid ${border}` }}>
                    <Typography sx={{ fontFamily: font, fontSize: "11px", color: textSecondary, fontWeight: 600 }}>
                      {day.toLocaleDateString(localeFor(schoolRegion), { weekday: "short" })}
                    </Typography>
                    <Typography sx={{
                      fontFamily: font, fontSize: "13px", fontWeight: 700,
                      color: isToday ? "#fff" : textPrimary,
                      backgroundColor: isToday ? accent : "transparent",
                      borderRadius: "999px", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mt: 0.25,
                    }}>
                      {day.getDate()}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box ref={scrollRef} sx={{ maxHeight: gridMaxHeight, overflowY: "auto" }}>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "52px repeat(7, minmax(90px, 1fr))", position: "relative" }}
                onPointerMove={updateDrag}
                onPointerUp={finishDrag}
                onPointerLeave={(e) => { if (e.buttons === 0) setDrag(null); }}
              >
                <Box sx={{
                  position: "relative", height: GRID_HEIGHT,
                  backgroundColor: surfaceRaised, borderRight: `1px solid ${border}`,
                }}>
                  {hourMarks.map((h) => (
                    <Typography
                      key={h}
                      sx={{
                        position: "absolute", top: (h - START_HOUR) * PX_PER_HOUR - 6, right: 8,
                        fontFamily: font, fontSize: "10.5px", color: textMuted, whiteSpace: "nowrap",
                      }}
                    >
                      {h === 0 ? "12 AM" : h === 12 ? "12 PM" : h > 12 ? `${h - 12} PM` : `${h} AM`}
                    </Typography>
                  ))}
                </Box>

                {days.map((day) => {
                  const key = dayKey(day);
                  const isToday = sameDate(day, today);
                  const isPastDay = day < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                  const daySlots = slotsByDay[key] || [];
                  const dragHere = drag && drag.dayKey === key ? drag : null;
                  const workWindow = workingWindowFor(day);

                  return (
                    <Box
                      key={key}
                      ref={(el) => { columnRefs.current[key] = el; }}
                      onPointerDown={(e) => beginDrag(day, e)}
                      sx={{
                        position: "relative", height: GRID_HEIGHT, borderLeft: `1px solid ${border}`,
                        cursor: isPastDay ? "default" : "crosshair",
                        backgroundColor: surface,
                        backgroundImage: `linear-gradient(to bottom, ${border} 1px, transparent 1px), linear-gradient(to bottom, ${border}90 1px, transparent 1px)`,
                        backgroundSize: `100% ${PX_PER_HOUR}px, 100% ${PX_PER_HOUR}px`,
                        backgroundPosition: `0 0, 0 ${PX_PER_HOUR / 2}px`,
                        userSelect: "none",
                        touchAction: "none",
                      }}
                    >
                      {/* Outside working hours = not bookable by default, shaded */}
                      {(() => {
                        const unavailable = [];
                        if (!workWindow) {
                          unavailable.push({ top: 0, height: GRID_HEIGHT });
                        } else {
                          if (workWindow.start > GRID_START_MIN) {
                            unavailable.push({ top: 0, height: (workWindow.start - GRID_START_MIN) * PX_PER_MIN });
                          }
                          if (workWindow.end < GRID_END_MIN) {
                            unavailable.push({ top: (workWindow.end - GRID_START_MIN) * PX_PER_MIN, height: (GRID_END_MIN - workWindow.end) * PX_PER_MIN });
                          }
                        }
                        return unavailable.map((band, i) => (
                          <Box key={i} sx={{
                            position: "absolute", left: 0, right: 0, top: band.top, height: band.height,
                            backgroundColor: "rgba(26,26,26,0.035)", pointerEvents: "none", zIndex: 1,
                          }} />
                        ));
                      })()}

                      {isToday && nowMin > GRID_START_MIN && (
                        <>
                          <Box sx={{
                            position: "absolute", top: 0, left: 0, right: 0,
                            height: clampMin((nowMin - GRID_START_MIN) * PX_PER_MIN, 0, GRID_HEIGHT),
                            backgroundColor: "rgba(26,26,26,0.025)", pointerEvents: "none", zIndex: 1,
                          }} />
                          <Box sx={{
                            position: "absolute", left: 0, right: 0,
                            top: clampMin((nowMin - GRID_START_MIN) * PX_PER_MIN, 0, GRID_HEIGHT),
                            height: "2px", backgroundColor: accent, pointerEvents: "none", zIndex: 3,
                          }}>
                            <Box sx={{ position: "absolute", left: -4, top: -3, width: 8, height: 8, borderRadius: "50%", backgroundColor: accent }} />
                          </Box>
                        </>
                      )}

                      {daySlots.map((slot) => {
                        const startMin = minutesFromDate(new Date(slot.start_time));
                        const endMin = minutesFromDate(new Date(slot.end_time));
                        const top = clampMin((startMin - GRID_START_MIN) * PX_PER_MIN, 0, GRID_HEIGHT);
                        const height = Math.max(((endMin - startMin) * PX_PER_MIN), 16);
                        const color = colorFor(slot);
                        const deletable = ["blocked", "reserved"].includes(slot.visibility) && !STATUS_STYLE[slot.status];
                        return (
                          <Box
                            key={slot._id}
                            data-slot-block
                            onPointerDown={(e) => e.stopPropagation()}
                            sx={{
                              position: "absolute", top, left: 3, right: 3, height,
                              borderRadius: "6px", backgroundColor: `${color}22`, border: `1px solid ${color}55`, borderLeft: `3px solid ${color}`,
                              px: 0.75, py: 0.25, overflow: "hidden", cursor: deletable ? "default" : "not-allowed", zIndex: 2,
                              "&:hover .delete-btn": { opacity: deletable ? 1 : 0 },
                            }}
                          >
                            <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color, lineHeight: 1.2 }} noWrap>
                              {labelFor(slot)}{slot.visibility === "reserved" && slot.reserved_for_name ? ` · ${slot.reserved_for_name}` : ""}
                            </Typography>
                            {height > 28 && (
                              <Typography sx={{ fontFamily: font, fontSize: "9.5px", color, lineHeight: 1.2, opacity: 0.85 }} noWrap>
                                {formatMin(startMin)} – {formatMin(endMin)}
                              </Typography>
                            )}
                            {deletable && (
                              <IconButton
                                className="delete-btn"
                                size="small"
                                onClick={(e) => { e.stopPropagation(); onDeleteSlot(slot._id); }}
                                sx={{
                                  position: "absolute", top: 1, right: 1, width: 16, height: 16, opacity: 0,
                                  transition: "opacity 0.12s", backgroundColor: surfaceRaised,
                                  "&:hover": { backgroundColor: `${color}33` },
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 11, color }} />
                              </IconButton>
                            )}
                          </Box>
                        );
                      })}

                      {packEvents(eventsByDay[key] || []).map(({ e, lane, lanes }) => {
                        const k = EVENT_KINDS[e.kind] || EVENT_KINDS.confirmed;
                        const startMin = minutesFromDate(e.start);
                        // A session running past midnight would key its end at
                        // a SMALLER minute than its start and render as a
                        // negative-height sliver; it is clipped at the day edge.
                        const rawEnd = minutesFromDate(e.end);
                        const endMin = rawEnd > startMin ? rawEnd : GRID_END_MIN;
                        const top = clampMin((startMin - GRID_START_MIN) * PX_PER_MIN, 0, GRID_HEIGHT);
                        const height = Math.max((endMin - startMin) * PX_PER_MIN, 16);
                        const w = 100 / lanes;
                        return (
                          <Box
                            key={e.id}
                            onPointerDown={(ev) => ev.stopPropagation()}
                            onClick={() => onPickEvent?.(e)}
                            title={`${e.title} · ${formatMin(startMin)}–${formatMin(endMin)} · ${k.label}${e.sub ? ` · ${e.sub}` : ""}`}
                            sx={{
                              position: "absolute", top, height,
                              left: `calc(${lane * w}% + 3px)`, width: `calc(${w}% - 6px)`,
                              borderRadius: "6px",
                              backgroundColor: `${k.color}22`,
                              backgroundImage: k.hatched
                                ? `repeating-linear-gradient(45deg, ${k.color}33 0 5px, transparent 5px 10px)`
                                : "none",
                              border: k.dashed ? `1px dashed ${k.color}` : `1px solid ${k.color}55`,
                              borderLeft: `3px solid ${k.color}`,
                              px: 0.75, py: 0.25, overflow: "hidden", zIndex: 2,
                              cursor: onPickEvent ? "pointer" : "default",
                              "&:hover": { boxShadow: "0 2px 10px rgba(0,0,0,0.16)", zIndex: 5 },
                            }}
                          >
                            <Typography noWrap sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: k.color, lineHeight: 1.2 }}>
                              {e.title}
                            </Typography>
                            {height > 28 && (
                              <Typography noWrap sx={{ fontFamily: font, fontSize: "9.5px", color: k.color, opacity: 0.85, lineHeight: 1.2 }}>
                                {formatMin(startMin)}{e.sub ? ` · ${e.sub}` : ""}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}

                      {dragHere && (() => {
                        const start = Math.min(dragHere.startMin, dragHere.endMin);
                        const end = Math.max(dragHere.startMin, dragHere.endMin, start + SNAP_MIN);
                        const top = (start - GRID_START_MIN) * PX_PER_MIN;
                        const height = Math.max((end - start) * PX_PER_MIN, SNAP_MIN * PX_PER_MIN);
                        // Neutral on purpose: mid-drag nothing has been decided
                        // yet, so the marquee must not imply an outcome.
                        const brushColor = accent;
                        return (
                          <Box sx={{
                            position: "absolute", top, left: 3, right: 3, height,
                            borderRadius: "6px", backgroundColor: `${brushColor}33`, border: `1.5px dashed ${brushColor}`,
                            zIndex: 4, display: "flex", alignItems: "center", justifyContent: "center", px: 0.5,
                          }}>
                            <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: brushColor, textAlign: "center" }}>
                              {formatMin(start)} – {formatMin(end)}
                            </Typography>
                          </Box>
                        );
                      })()}

                      {/* The range the composer is open on, kept drawn so the
                          form is filled in about something visible. */}
                      {pending && dayKey(pending.day) === key && (() => {
                        const pStart = minutesFromDate(pending.start);
                        const pEnd = minutesFromDate(pending.end);
                        const top = (pStart - GRID_START_MIN) * PX_PER_MIN;
                        const height = Math.max((pEnd - pStart) * PX_PER_MIN, SNAP_MIN * PX_PER_MIN);
                        return (
                          <Box sx={{
                            position: "absolute", top, left: 3, right: 3, height,
                            borderRadius: "6px", backgroundColor: `${accent}22`, border: `1.5px dashed ${accent}`,
                            zIndex: 4, display: "flex", alignItems: "center", justifyContent: "center", px: 0.5,
                            pointerEvents: "none",
                          }}>
                            <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: accent, textAlign: "center" }}>
                              {formatMin(pStart)} – {formatMin(pEnd)}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mt: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          {/* The legend is generated from what is on the grid, not hardcoded.
              A fixed legend drifts the moment a kind is added, and a key that
              names a colour nothing is drawn in is worse than none. */}
          {[
            { label: "Available", bg: surface, bd: border },
            { label: "Outside hours", bg: "rgba(26,26,26,0.06)", bd: border },
            { label: "Closed", bg: textMuted, bd: textMuted },
            { label: "Reserved", bg: "#d97706", bd: "#d97706" },
            ...(events.length
              // Canonical order, not order of first appearance. Deriving it
              // from the data means the key reshuffles between instructors and
              // between weeks, and a legend you have to re-read every time is
              // not a legend.
              ? Object.keys(EVENT_KINDS)
                .filter((k) => events.some((e) => e.kind === k))
                .map((k) => ({
                  label: EVENT_KINDS[k].label,
                  bg: EVENT_KINDS[k].dashed ? "transparent" : `${EVENT_KINDS[k].color}33`,
                  bd: EVENT_KINDS[k].color,
                  dashed: EVENT_KINDS[k].dashed,
                  hatched: EVENT_KINDS[k].hatched,
                  color: EVENT_KINDS[k].color,
                }))
              : [{ label: "Booked", bg: "#2563eb", bd: "#2563eb" }]),
          ].map((item) => (
            <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{
                width: 10, height: 10, borderRadius: "3px", backgroundColor: item.bg,
                backgroundImage: item.hatched
                  ? `repeating-linear-gradient(45deg, ${item.color}88 0 2px, transparent 2px 4px)`
                  : "none",
                border: `1px ${item.dashed ? "dashed" : "solid"} ${item.bd}`,
              }} />
              <Typography sx={{ fontFamily: font, fontSize: "11px", color: textSecondary }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted }}>
          Drag any free time to schedule something. Hover a block to remove it.
        </Typography>
      </Box>
    </Box>
  );
}
